import qs from "qs";
import { AxiosResponse } from "axios";
import { AxiosInstance } from "./axiosInstance";
import {
  AddPaymentCompleteRequest,
  AddPaymentCompleteResponse,
  AddPaymentMethodRequest,
  AddPaymentMethodResponse,
  GetPlanUsageGraphResponse,
  GetPlanServicesResponse,
  PostPlanStatCardResponse,
  StatCardRequestDto,
  GetPlanEventHistoryResponse,
  GetPlanEuResponse,
  DefaultCycleRequest,
  DowngradeResponse,
  AutoEnrollRequestDto,
  GetPlanUsageStorageResponse,
  ChannelUsageRequestDto,
  GetPlanDetailSummaryResponse,
  GetPlanInvoice,
} from "./interfaces/planInterface.interface";
import {
  BasePagination,
  CommonResponse,
} from "./interfaces/commonInterface.interface";

const PLAN_API_PATH = "/plan";

export const getPlanServices = async ({
  accountId,
}: {
  accountId: string;
}): Promise<GetPlanServicesResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanServicesResponse> =
      await AxiosInstance.get(`${PLAN_API_PATH}/services/${accountId}`);
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getPlanEventHistory = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: BasePagination;
}): Promise<GetPlanEventHistoryResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanEventHistoryResponse> =
      await AxiosInstance.get(
        `${PLAN_API_PATH}/eventHistory/${accountId}?${qs.stringify(payload)}`
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getPlanEndUser = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: BasePagination;
}): Promise<GetPlanEuResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanEuResponse> = await AxiosInstance.get(
      `${PLAN_API_PATH}/endUser/${accountId}?${qs.stringify(payload)}`
    );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getPlanDetailSummary = async ({
  accountId,
}: {
  accountId: string;
}): Promise<GetPlanDetailSummaryResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanDetailSummaryResponse> =
      await AxiosInstance.get(`${PLAN_API_PATH}/detail/summary/${accountId}`);
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getPlanInvoice = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: {
    billingDate: string;
  };
}): Promise<GetPlanInvoice> => {
  try {
    const { data }: AxiosResponse<GetPlanInvoice> = await AxiosInstance.get(
      `${PLAN_API_PATH}/invoice/${accountId}?${qs.stringify(payload)}`
    );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanStatCard = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: StatCardRequestDto;
}): Promise<PostPlanStatCardResponse> => {
  try {
    const { data }: AxiosResponse<PostPlanStatCardResponse> =
      await AxiosInstance.post(
        `${PLAN_API_PATH}/statCard/${accountId}`,
        payload
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanAddPayment = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: AddPaymentMethodRequest;
}): Promise<AddPaymentMethodResponse> => {
  try {
    const { data }: AxiosResponse<AddPaymentMethodResponse> =
      await AxiosInstance.post(
        `${PLAN_API_PATH}/addPayment/${accountId}`,
        payload
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanAddPaymentComplete = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: AddPaymentCompleteRequest;
}): Promise<AddPaymentCompleteResponse> => {
  try {
    const { data }: AxiosResponse<AddPaymentCompleteResponse> =
      await AxiosInstance.post(
        `${PLAN_API_PATH}/addPayment/complete/${accountId}`,
        payload
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanCustomPortal = async ({
  accountId,
}: {
  accountId: string;
}): Promise<AddPaymentMethodResponse> => {
  try {
    const { data }: AxiosResponse<AddPaymentMethodResponse> =
      await AxiosInstance.post(`${PLAN_API_PATH}/customPortal/${accountId}`);
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanUsageStorage = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: DefaultCycleRequest;
}): Promise<GetPlanUsageStorageResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanUsageStorageResponse> =
      await AxiosInstance.post(
        `${PLAN_API_PATH}/usage/storage/${accountId}`,
        payload
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postPlanUsageChannel = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: ChannelUsageRequestDto;
}): Promise<GetPlanUsageGraphResponse> => {
  try {
    const { data }: AxiosResponse<GetPlanUsageGraphResponse> =
      await AxiosInstance.post(
        `${PLAN_API_PATH}/usage/channel/${accountId}`,
        payload
      );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postDowngradeCycle = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: DefaultCycleRequest;
}): Promise<DowngradeResponse> => {
  try {
    const { data }: AxiosResponse<DowngradeResponse> = await AxiosInstance.post(
      `${PLAN_API_PATH}/downgrade/${accountId}`,
      payload
    );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postAutoEnroll = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: AutoEnrollRequestDto;
}): Promise<CommonResponse> => {
  try {
    const { data }: AxiosResponse<CommonResponse> = await AxiosInstance.post(
      `${PLAN_API_PATH}/autoEnroll/${accountId}`,
      payload
    );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const postAddCloudStorage = async ({
  accountId,
  payload,
}: {
  accountId: string;
  payload: { storage: number; accountId: string };
}): Promise<CommonResponse> => {
  try {
    const { data }: AxiosResponse<CommonResponse> = await AxiosInstance.post(
      `${PLAN_API_PATH}/cloudStorage/add/${accountId}`,
      payload
    );
    return data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
